import { LuigiConfig } from './';
import { AuthStoreSvc, AuthLayerSvc } from '../services';

/* istanbul ignore file */
/**
 * Authorization helpers.
 * @default Luigi.auth()
 * @name Authorization
 * @class
 */
class LuigiAuth {
  /**
   * Detects if authorization is enabled via configuration.
   * Read more about [custom authorization providers](authorization-configuration.md).
   * @memberof Authorization
   * @returns {boolean} `true` if authorization is enabled - otherwise returns `false`
   * @example
   * Luigi.auth().isAuthorizationEnabled();
   */
  isAuthorizationEnabled() {
    return !!LuigiConfig.getConfigValue('auth.use');
  }

  /**
   * Login the user dynamically.
   * This will run the same functionality as though the user clicked the login button.
   * @memberof Authorization
   * @since 1.5.0
   * @example
   * Luigi.auth().login();
   */
  login() {
    if (this.isAuthorizationEnabled()) {
      AuthLayerSvc.startAuthorization();
    }
  }

  /**
   * Logout the user dynamically.
   * This will run the same functionality as though the user clicked the logout button.
   * @memberof Authorization
   * @since 1.5.0
   * @example
   * Luigi.auth().logout();
   */
  logout() {
    if (this.isAuthorizationEnabled()) {
      AuthLayerSvc.logout();
    }
  }

  /**
   * Authorization object that is stored in auth store and used within Luigi. It is then available in [LuigiClient.addInitListener](luigi-client-api.md#addInitListener) and can also be used in the Core configuration. <br><br>
   * Type: [Object](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object)
   * @typedef {Object} AuthData
   * @memberof Authorization
   * @kind typedef
   * @property {string} accessToken - access token value
   * @property {string} accessTokenExpirationDate - timestamp value
   * @property {string} scope - scope, can be empty if it is not required
   * @property {string} idToken - id token, used for renewing authentication
   */

  /**
   * @private
   * @memberof Authorization
   * @param {string} eventName
   * @param {Object} providerInstanceSettings
   * @param {AuthData} data
   * @param {string} redirectUrl
   */
  async handleAuthEvent(eventName, providerInstanceSettings, data, redirectUrl) {
    const result = await LuigiConfig.executeConfigFnAsync(
      'auth.events.' + eventName,
      false,
      providerInstanceSettings,
      data
    );
    let redirect = result === undefined || !!result;
    if (redirect && redirectUrl) {
      window.location.href = redirectUrl;
      return;
    }
    return redirect;
  }

  /**
   * @namespace AuthorizationStore
   */
  /**
   * Getter for authorization Storage helpers, to be used in your custom authorization provider.
   * Read more about custom authorization providers [here](authorization-configuration.md#implement-a-custom-authorization-provider).
   * @memberof AuthorizationStore
   * @returns {Object} authorization storage helpers
   */
  get store() {
    if (!LuigiConfig.initialized) {
      console.warn(
        'Luigi Core is not initialized yet. Consider moving your code to the luigiAfterInit lifecycle hook. ' +
          'Documentation: https://docs.luigi-project.io/docs/lifecycle-hooks'
      );
    }

    return {
      /**
       * Retrieves the key name that is used to store the auth data.
       * @memberof AuthorizationStore
       * @returns {string} name of the store key
       * @example Luigi.auth().store.getStorageKey()
       */
      getStorageKey: () => AuthStoreSvc.getStorageKey(),
      /**
       * Retrieves the storage type that is used to store the auth data. To set it, use the `storage` property of the `auth` Luigi configuration object. Find out more [here](https://docs.luigi-project.io/docs/authorization-configuration?section=general-authorization-options).
       * @memberof AuthorizationStore
       * @returns {('localStorage'|'sessionStorage'|'none')} storage type
       * @example Luigi.auth().store.getStorageType()
       */
      getStorageType: () => AuthStoreSvc.getStorageType(),
      /**
       * Retrieves the current auth object.
       * @memberof AuthorizationStore
       * @returns {AuthData} the current auth data object
       * @example Luigi.auth().store.getAuthData()
       */
      getAuthData: () => AuthStoreSvc.getAuthData(),
      /**
       * Sets authorization data
       * @memberof AuthorizationStore
       * @param {AuthData} data - new auth data object
       * @example Luigi.auth().store.setAuthData(data)
       */
      setAuthData: (data) => AuthStoreSvc.setAuthData(data),
      /**
       * Clears authorization data from store
       * @memberof AuthorizationStore
       * @example Luigi.auth().store.removeAuthData()
       */
      removeAuthData: () => AuthStoreSvc.removeAuthData(),
      /**
       * Defines a new authorization session. Must be triggered after initial `setAuthData()` in order to trigger **onAuthSuccessful** event after login.
       * @memberof AuthorizationStore
       * @example Luigi.auth().store.setNewlyAuthorized()
       */
      setNewlyAuthorized: () => {
        AuthStoreSvc.setNewlyAuthorized();
        AuthLayerSvc.resetExpirationChecks();
      }
    };
  }
}

export const auth = new LuigiAuth();
